import { useQuery } from "@apollo/client";
import { loader } from "graphql.macro";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import db from "../util/storage";
import Ingredient from "../components/Ingredient";
import Loading from "../components/Loaders/Loading";
import Card from "../components/RecipeView/Card";

const GET_RECIPE = loader("../queries/recipe.graphql");

function IngredientGroup({ group, multiplier }) {
	return (
		<div>
			{group.name ? <strong>{group.name}</strong> : null}
			{group.recipeIngredients.map((ingredient, i) => {
				return (
					<div key={i}>
						<Ingredient ingredient={ingredient} multiplier={multiplier} />
					</div>
				);
			})}
		</div>
	);
}

function Instruction({ instruction }) {
	return <div dangerouslySetInnerHTML={{ __html: instruction.content }}></div>;
}

function Yields({ text, multiplier }) {
	return (
		<>
			{text}
			{multiplier !== 1 ? <span>&nbsp;&times;{multiplier}</span> : null}
		</>
	);
}

function Hero({ recipe }) {
	const [isInMealPlan, setInMealPlan] = useState(
		db.hasItem(recipe.id)
	);

	const toggleMealPlan = () => {
		setInMealPlan(!isInMealPlan);
		db.toggleItem(recipe.id);
	};

	return (
		<section
			className="hero is-dark is-medium is-bold parallax"
			style={{
				backgroundImage: `url(${
					recipe?.photo?.heroImageUrl || recipe.photo?.imageUrl
				})`,
				backgroundPosition: "center",
				backgroundSize: "cover",
				backgroundBlendMode: "soft-light",
			}}>
			<div className="hero-body">
				<div className="container">
					<h1 className="title">{recipe.title}</h1>
					<h2
						className="subtitle"
						dangerouslySetInnerHTML={{ __html: recipe.description }}></h2>
					<button
						className="button is-primary"
						onClick={() => toggleMealPlan()}>
						{isInMealPlan ? "Remove from Meal Plan" : "Add to Meal Plan"}
					</button>
				</div>
			</div>
		</section>
	);
}

export default function Recipe() {
	const { id } = useParams();

	let [multiplier, setMultiplier] = useState(1);

	const { loading, error, data } = useQuery(GET_RECIPE, {
		variables: { id: id },
	});

	const getMultiplier = (isIncrement) => {
		if (isIncrement) {
			if (multiplier < 1) {
				multiplier *= 2;
			} else {
				multiplier++;
			}
		} else {
			if (multiplier <= 1) {
				multiplier /= 2;
			} else {
				multiplier--;
			}
		}
		setMultiplier(multiplier);
	};

	if (loading) {
		return <Loading />;
	}

	if (error) {
		return `Error! ${error.message}`;
	}

	const recipe = data?.search?.hits[0]?._source;

	return (
		<>
			<Hero recipe={recipe} />
			<div className="container content">
				{recipe.overview ? (
					<section className="section">
						<h3 className="title is-3">Overview</h3>
						<p dangerouslySetInnerHTML={{ __html: recipe.overview }}></p>
					</section>
				) : null}
				<section className="section">
					<div className="columns">
						<div className="column is-two-thirds instructions">
							<h3 className="title is-3">Instructions</h3>
							<div
								dangerouslySetInnerHTML={{
									__html: recipe.instructions?.content,
								}}></div>
							{recipe.instructions?.map((instruction, i) => (
								<Instruction instruction={instruction} key={i} />
							))}
						</div>
						<div className="column is-one-third">
							<div className="ingredients-list">
								<h3 className="title is-3">Ingredients</h3>
								<div className="field has-addons">
									<div className="control">
										<button
											type="button"
											className="button is-small is-primary"
											onClick={() => getMultiplier(false)}>
											<span className="icon is-small">
												<i className="fas fa-minus"></i>
											</span>
										</button>
									</div>
									<div className="control">
										<span className="button is-small is-static">
											<Yields text={recipe.yields} multiplier={multiplier} />
										</span>
									</div>
									<div className="control">
										<button
											type="button"
											className="button is-small is-primary"
											onClick={() => getMultiplier(true)}>
											<span className="icon is-small">
												<i className="fas fa-plus"></i>
											</span>
										</button>
									</div>
								</div>
								<div className="scroller">
									{recipe.recipeIngredientGroups?.map((group, i) => (
										<IngredientGroup
											group={group}
											multiplier={multiplier}
											key={i}
										/>
									))}
								</div>
							</div>
						</div>
					</div>
				</section>
				<section className="section">
					<h3 className="title is-3">Related</h3>
					<div className="columns is-multiline">
						{recipe.relateds
							?.map((r) => r.related)
							.map((r, i) => {
								return (
									<div className="column is-one-quarter" key={i}>
										<Card recipe={r} />
									</div>
								);
							})}
					</div>
				</section>
			</div>
		</>
	);
}
